import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import StatusCode from "../../../util/StatusCode";
import TableVertical, { TableVerticalInfoObject } from "../../misc/TableVertical";
import * as Helper from '../../../util/Helper';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import TableHorizontal, { InfoObject } from "../../misc/TableHorizontal";
import { CorePersonSensitiveField, CoreUser } from "../../../models/CoreUser";
import { GetUser } from "../../../store/UserAction";
import { useTranslation } from "react-i18next";
import { RequestState } from "../../../models/RequestState";
import UserViewSensitiveField from "./UserViewSensitiveFields";
import { CoreLoggedInUser } from "../../../models/CoreLoggedInUser";
import { FlowSyncAccessPolicy } from "../../../models/FlowSyncAccessPolicy";
import { RootState } from "../../../store/store";



const UserViewInfo = () => {
    const { t } = useTranslation(['common', 'schoolAdministration']);

    const { userId } = useParams();
    const dispatch = useDispatch();
    const user = useSelector<RootState, RequestState<CoreUser>>(state => state.users.user);
    const me = useSelector<RootState, CoreLoggedInUser | undefined>(state => state.me.me);
    const [show, setShow] = useState({
        info: true,
        eppns: true,
        emails: true,
        addresses: true,
        phoneNumbers: true,
        externalIdentifiers: true
    })
    const allowedSensitiveInfo = me?.authorizedAccessPolicies.some(x => x === FlowSyncAccessPolicy.ALLOWED_GET_SENSITIVE_FIELDS) ?? false;

    useEffect(() => {
        if (userId) {
            GetUser(userId)(dispatch);
        }
    }, [dispatch, userId]);

    const renderUserInfo = () => {
        const userInfo: TableVerticalInfoObject[] = [];
        if (user.code === StatusCode.COMPLETE && userId !== undefined) {
            const u = user.data;
            userInfo.push(
                { header: t('userViewInformationTableHeaders.displayName', { ns: 'schoolAdministration' }), content: u.displayName },
                { header: t('genericTableHeaders.active', { ns: 'common' }), content: Helper.active(u.active) },
                { header: t('genericTableHeaders.created', { ns: 'common' }), content: Helper.formatDate(u.created) },
                { header: t('genericTableHeaders.modified', { ns: 'common' }), content: Helper.formatDate(u.modified) },
                { header: t('genericTableHeaders.removed', { ns: 'common' }), content: Helper.formatDate(u?.removed) },
                { header: t('userViewInformationTableHeaders.securityMarking', { ns: 'schoolAdministration' }), content: u.securityMarking ? 'Ja' : 'Nej' },
                { header: t('userViewInformationTableHeaders.givenName', { ns: 'schoolAdministration' }), content: u.givenName },
                { header: t('userViewInformationTableHeaders.middleName', { ns: 'schoolAdministration' }), content: u.middleName },
                { header: t('userViewInformationTableHeaders.familyName', { ns: 'schoolAdministration' }), content: u.familyName },
                { header: t('userViewInformationTableHeaders.sex', { ns: 'schoolAdministration' }), content: t(`personSex.${u.sex ?? 0}`, { ns: 'schoolAdministration' }) },
                { header: t('genericTableHeaders.id', { ns: 'common' }), content: u.id },
                { header: t('userViewInformationTableHeaders.hashedCivicNo', { ns: 'schoolAdministration' }), content: u.hashedCivicNo },
                { header: t('userViewInformationTableHeaders.civicNo', { ns: 'schoolAdministration' }), content: <UserViewSensitiveField userId={userId} field={CorePersonSensitiveField.CivicNo} disabled={!u.hasCivicNo || !allowedSensitiveInfo} /> },
                { header: t('userViewInformationTableHeaders.birthDate', { ns: 'schoolAdministration' }), content: Helper.formatDateOnly(u.birthDate) },
                { header: t('userViewInformationTableHeaders.personStatus', { ns: 'schoolAdministration' }), content: t(`personStatus.${u.personStatus}`, { ns: 'schoolAdministration' }) },
                { header: t('userViewInformationTableHeaders.photo', { ns: 'schoolAdministration' }), content: u.photo && <Link to={u.photo} target="_blank"><>Visa foto <FontAwesomeIcon icon="arrow-up-right-from-square" className="icon" /></></Link> },
            )
        }

        return (
            <TableVertical
                header={t('commonTableHeaders.information', { ns: 'schoolAdministration' })}
                info={userInfo}
                error={user.code === StatusCode.ERROR}
                searching={user.code === StatusCode.PENDING}
                displayToggle={true}
                show={show.info}
                toggle={() => setShow({ ...show, info: !show.info })}
            />
        )
    }

    const renderUserEPPNs = () => {
        let eppnList: InfoObject[] = [];
        if (user.code === StatusCode.COMPLETE) {
            eppnList = user.data.eduPrincipalNames?.map((u, i) => ({
                id: i.toString(),
                data: [
                    { value: t(`personEPPN.${u.type ?? 0}`, { ns: 'schoolAdministration' }) },
                    { value: u.value },
                ]
            })) ?? [];
        }

        return (
            <TableHorizontal
                header={[{ value: t('commonTableHeaders.type', { ns: 'schoolAdministration' }) }, { value: t('userViewEppnTable.eduPersonPrincipalName', { ns: 'schoolAdministration' }) }]}
                info={eppnList}
                error={user.code === StatusCode.ERROR}
                searching={user.code === StatusCode.PENDING}
                displayName={t('userViewEppnTable.eduPersonPrincipalName', { ns: 'schoolAdministration' })}
                showHeader={true}
                noResMsg={t('notFound.general', { ns: 'schoolAdministration', type: t('type.eppn', { ns: 'schoolAdministration' }) })}
                displayToggle={true}
                show={show.eppns}
                toggle={() => setShow({ ...show, eppns: !show.eppns })}
            />
        )
    }

    const renderUserEmails = () => {
        let emailsList: InfoObject[] = [];
        if (user.code === StatusCode.COMPLETE) {
            emailsList = user.data.emails?.map((u, i) => ({
                id: i.toString(),
                data: [
                    { value: t(`personEmail.${u.type ?? 0}`, { ns: 'schoolAdministration' }) },
                    { value: u.value },
                ]
            })) ?? [];
        }
        return (
            <TableHorizontal
                header={[{ value: t('commonTableHeaders.type', { ns: 'schoolAdministration' }) }, { value: t('commonTableHeaders.e-postaddress', { ns: 'schoolAdministration' }) }]}
                info={emailsList}
                error={user.code === StatusCode.ERROR}
                searching={user.code === StatusCode.PENDING}
                displayName={t('userViewEmailTable.emails', { ns: 'schoolAdministration' })}
                showHeader={true}
                noResMsg={t('notFound.general', { ns: 'schoolAdministration', type: t('type.e-postaddress', { ns: 'schoolAdministration' }) })}
                displayToggle={true}
                show={show.emails}
                toggle={() => setShow({ ...show, emails: !show.emails })}
            />
        )
    }

    const renderUserAddresses = () => {
        let addressList: InfoObject[] = []
        if (user.code === StatusCode.COMPLETE) {
            addressList = user.data.addresses?.map((u, i) => ({
                id: i.toString(),
                data: [
                    { value: t(`personAddress.${u.type ?? 0}`, { ns: 'schoolAdministration' }) },
                    { value: u.streetAddress },
                    { value: u.postalCode },
                    { value: u.locality },
                ]
            })) ?? [];
        }
        return (
            <TableHorizontal
                header={[{ value: t('commonTableHeaders.type', { ns: 'schoolAdministration' }) }, { value: t('userViewAddressTable.streetAddress', { ns: 'schoolAdministration' }) }, { value: t('userViewAddressTable.postalCode', { ns: 'schoolAdministration' }) }, { value: t('userViewAddressTable.locality', { ns: 'schoolAdministration' }) }]}
                info={addressList}
                error={user.code === StatusCode.ERROR}
                searching={user.code === StatusCode.PENDING}
                displayName={t('userViewAddressTable.addresses', { ns: 'schoolAdministration' })}
                showHeader={true}
                noResMsg={t('notFound.general', { ns: 'schoolAdministration', type: t('type.addresses', { ns: 'schoolAdministration' }) })}
                displayToggle={true}
                show={show.addresses}
                toggle={() => setShow({ ...show, addresses: !show.addresses })}
            />
        )
    }

    const renderUserPhoneNumbers = () => {
        let phoneNumberList: InfoObject[] = [];
        if (user.code === StatusCode.COMPLETE) {
            phoneNumberList = user.data.phoneNumbers?.map((u, i) => ({
                id: i.toString(),
                data: [
                    { value: t(`personPhoneNumber.${u.type}`, { ns: 'schoolAdministration' }) },
                    { value: u.value },
                    { value: Helper.active(u.mobile) },
                ]
            })) ?? [];
        }
        return (
            <TableHorizontal
                header={[{ value: t('commonTableHeaders.type', { ns: 'schoolAdministration' }) }, { value: t('userViewPhoneNumber.phoneNumber', { ns: 'schoolAdministration' }) }, { value: t('userViewPhoneNumber.mobile', { ns: 'schoolAdministration' }) }]}
                info={phoneNumberList}
                error={user.code === StatusCode.ERROR}
                searching={user.code === StatusCode.PENDING}
                displayName={t('userViewPhoneNumber.phoneNumber', { ns: 'schoolAdministration' })}
                showHeader={true}
                noResMsg={t('notFound.general', { ns: 'schoolAdministration', type: t('type.phoneNumber', { ns: 'schoolAdministration' }) })}
                displayToggle={true}
                show={show.phoneNumbers}
                toggle={() => setShow({ ...show, phoneNumbers: !show.phoneNumbers })}
            />
        )
    }

    const renderPersonExternalIdentifiers = () => {
        let externalIdentifierList: InfoObject[] = [];
        if (user.code === StatusCode.COMPLETE) {
            externalIdentifierList = user.data.externalIdentifiers?.map((u, i) => ({
                id: i.toString(),
                data: [
                    { value: u.context },
                    { value: u.value },
                    { value: Helper.active(u.active) },
                    { value: Helper.active(u.globallyUnique) },
                    { value: Helper.formatDateOnly(u.created) },
                    { value: Helper.formatDateOnly(u.modified) },
                    { value: Helper.formatDateOnly(u.removed) },
                ]
            })) ?? [];
        }
        return (
            <TableHorizontal
                header={[
                    { value: t('commonTableHeaders.type', { ns: 'schoolAdministration' }) },
                    { value: t('personExternalIdentifier.value', { ns: 'schoolAdministration' }) },
                    { value: t('genericTableHeaders.active', { ns: 'common' }) },
                    { value: t('personExternalIdentifier.globallyUnique', { ns: 'schoolAdministration' }) },
                    { value: t('genericTableHeaders.created', { ns: 'common' }) },
                    { value: t('genericTableHeaders.modified', { ns: 'common' }) },
                    { value: t('genericTableHeaders.removed', { ns: 'common' }) },
                ]}
                info={externalIdentifierList}
                error={user.code === StatusCode.ERROR}
                searching={user.code === StatusCode.PENDING}
                displayName={t('personExternalIdentifier.personExternalIdentifier', { ns: 'schoolAdministration' })}
                showHeader={true}
                noResMsg={t('notFound.general', { ns: 'schoolAdministration', type: t('type.externalIdentifier', { ns: 'schoolAdministration' }) })}
                displayToggle={true}
                show={show.externalIdentifiers}
                toggle={() => setShow({ ...show, externalIdentifiers: !show.externalIdentifiers })}
            />
        )
    }

    return (<>
        {renderUserInfo()}
        {renderUserEPPNs()}
        {renderUserEmails()}
        {renderUserAddresses()}
        {renderUserPhoneNumbers()}
        {renderPersonExternalIdentifiers()}
    </>)
}

export default UserViewInfo;
